import { MaterialCommunityIcons } from '@expo/vector-icons';
import {
  ActivityOrderBy,
  formatShortDate,
  getEffectiveCreationDate,
  Issue,
  IssueType,
  replaceMentionPlaceholderWithNames,
  Severity,
  useCurrentAccount,
  useFindByQuery,
} from '@gripp/shared-logic';
import * as Haptics from 'expo-haptics';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Platform,
  Pressable,
  StyleProp,
  StyleSheet,
  View,
  ViewStyle,
} from 'react-native';
import DropDownPicker from 'react-native-dropdown-picker';
import { IconButton, Text } from 'react-native-paper';
import { Layout } from '../../styles';
import { Colors } from '../../themes/colors';
import { HeaderDot } from '../HeaderDot';
import LoadingIndicator from '../loadingIndicator';
import { getIssueAuditData } from './issueFormatUtils';
import { IssueMenu } from './issueMenu';
import { ReminderSummary } from './reminderSummary';
import { SeverityIcons } from './severityIcons';

export type IssueHeaderProps = {
  issue: Issue;
  onChanged: (severity: string) => void;
  setShowFooter: (show: boolean) => void;
  titleStyle?: StyleProp<ViewStyle>;
  viewAssetInfo?: () => void;
  onIssueDelete: () => void;
  hideMenu?: boolean;
  onPrint?: () => Promise<void>;
};

type OptionButtonProps = {
  selected: string;
  severity: Severity;
  icon: string;
};

const OptionButton = (props: OptionButtonProps) => (
  <IconButton
    icon={props.icon}
    iconColor={Colors.white}
    containerColor={Colors[props.severity]}
    style={[
      styles.dropdownButton,
      props.severity === Severity.Red && styles.redPickerButton,
      props.severity === Severity.Yellow && styles.yellowPickerButton,
      props.severity === Severity.Green && styles.greenPickerButton,
    ]}
  />
);

export const IssueHeaderOld = (props: IssueHeaderProps) => {
  const { t } = useTranslation();
  const { account } = useCurrentAccount();
  const isRestrictedUser =
    account?.effectivePermissions?.includes('*.restricted');
  const issue = props.issue;
  if (!issue) {
    return;
  }

  const getProcessedTitle = () => {
    const titleText = issue.title;

    // Process mentions to ensure clean format
    if (titleText.includes('@[') && (issue as any).userMentions) {
      return replaceMentionPlaceholderWithNames(
        titleText,
        (issue as any).userMentions,
        t('mentions.unknownUser'),
        true
      );
    }

    return titleText;
  };

  const issueId = issue.id;

  const filter = useMemo(() => {
    return {
      trigger: {
        type: {
          eq: 'ISSUE',
        },
      },
      and: [
        {
          disabled: {
            ne: true,
          },
        },
      ],
      json: { 'trigger.config.issueId': issueId },
    };
  }, [issueId]);

  const { data: workflows, isLoading } = useFindByQuery({
    modelName: 'workflow',
    query: FIND_WORKFLOWS,
    variables: { filter },
  });

  const hasReminder = useMemo(() => workflows?.items?.length > 0, [workflows]);

  const workflow = useMemo(() => workflows?.items[0], [workflows]);

  const createdAt = getEffectiveCreationDate(
    issue.adjustCreationDate,
    issue.createdAt?.toString() || ''
  );

  const [currentSeverity, setSeverity] = useState(issue.severity);
  const [showSeverity, setShowSeverity] = useState(false);

  const severityList = useMemo(() => {
    const statuses = [Severity.Red, Severity.Yellow, Severity.Green];

    return statuses.map((x, i) => ({
      label: '',
      value: x,
      icon: () => (
        <OptionButton
          selected={currentSeverity}
          severity={x}
          icon={SeverityIcons[x]}
        />
      ),
    }));
  }, [currentSeverity]);

  useEffect(() => {
    setSeverity(issue.severity);
    setShowSeverity(false);
  }, [issue]);

  const toggleIcon = () => {
    if (showSeverity) {
      return 'chevron-up';
    } else {
      return 'chevron-down';
    }
  };

  const isSeverityDisabled = () => {
    return (
      !issue ||
      issue.type === IssueType.Feedback ||
      issue.type === IssueType.Routine
    );
  };

  const { who } = getIssueAuditData(issue, ActivityOrderBy.MostRecentReported);

  return (
    <View style={styles.container}>
      <View style={styles.headerContainer}>
        <View style={styles.dropdownContainer}>
          <DropDownPicker
            open={showSeverity}
            setOpen={setShowSeverity}
            value={currentSeverity}
            setValue={setSeverity}
            items={severityList}
            onSelectItem={(item) => {
              props.onChanged(item.value as string);
            }}
            onClose={() => {
              Platform.OS === 'ios' &&
                Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light);
            }}
            showArrowIcon={false}
            showTickIcon={false}
            containerStyle={{
              width: 70,
            }}
            dropDownContainerStyle={{
              marginTop: 32,
              borderWidth: 0,
              backgroundColor: 'transparent',
            }}
            listItemContainerStyle={{
              paddingHorizontal: 0,
            }}
            props={{
              style: { display: 'none' },
              disabled: true,
            }}
            itemProps={{
              activeOpacity: 0.7,
            }}
          />
          <View style={styles.floatingButtonContainer}>
            <Pressable
              style={[
                styles.dropdownButton,
                { backgroundColor: Colors[currentSeverity] },
              ]}
              pointerEvents={isSeverityDisabled() ? 'none' : 'auto'}
              onPress={
                isSeverityDisabled()
                  ? undefined
                  : () => {
                      Platform.OS === 'ios' &&
                        Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light);
                      setShowSeverity((prev) => !prev);
                    }
              }
            >
              <MaterialCommunityIcons
                name={SeverityIcons[currentSeverity]}
                color={Colors.white}
                size={20}
              />
              {isSeverityDisabled() ? undefined : (
                <MaterialCommunityIcons
                  name={toggleIcon()}
                  color={Colors.white}
                  size={24}
                  style={{ opacity: 0.5 }}
                />
              )}
            </Pressable>
          </View>
        </View>
        <View style={styles.contentContainer}>
          <View style={styles.titleRow}>
            <Text
              style={[styles.titleStyle]}
              numberOfLines={1}
              ellipsizeMode="tail"
            >
              {getProcessedTitle()}
            </Text>
          </View>

          <View style={styles.infoRow}>
            <Text style={styles.infoText}>
              {issue.asset?.name || issue.workspace.name}
            </Text>
            <HeaderDot />
            <Text style={styles.infoText}>{who}</Text>
            <HeaderDot />
            <Text style={styles.infoText}>{formatShortDate(createdAt)}</Text>
          </View>
        </View>
        <View>
          {!props.hideMenu && (
            <View style={styles.menuContainer}>
              {/* Show pinned icon if issue is pinned */}
              {issue.isPinned && (
                <MaterialCommunityIcons
                  name="pin"
                  color={Colors.black}
                  size={20}
                  style={styles.pinnedIcon}
                />
              )}

              <IssueMenu
                issue={issue}
                hasReminder={hasReminder}
                onIssueDelete={props.onIssueDelete}
                onPrint={props.onPrint}
              />
            </View>
          )}
        </View>
      </View>
      {isLoading ? (
        <LoadingIndicator />
      ) : (
        !isRestrictedUser &&
        workflow && <ReminderSummary workflow={workflow} issue={props.issue} />
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    backgroundColor: Colors.white,
    borderBottomColor: Colors.grayDisabled,
    borderBottomWidth: 0.5,
    minHeight: 80,
    ...Platform.select({
      web: {
        zIndex: 1,
      },
    }),
  },
  headerContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: 20,
    paddingVertical: 20,
    overflow: 'visible' as const,
    minHeight: 80,
    zIndex: 1,
  },
  contentContainer: {
    flexDirection: 'column',
    alignItems: 'flex-start',
    justifyContent: 'center',
    ...Layout.container,
    width: '100%',
    marginLeft: 4,
    height: 50,
    overflow: 'hidden',
  },
  dropdownContainer: {
    height: 42,
    flexDirection: 'row',
    justifyContent: 'flex-start',
    alignItems: 'center',
  },
  floatingButtonContainer: {
    position: 'absolute',
    top: 0,
    left: 0,
    bottom: 0,
    right: 0,
    flexDirection: 'row',
    justifyContent: 'flex-start',
    alignItems: 'center',
  },
  dropdownButton: {
    width: 60,
    height: 40,
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: 0,
    borderRadius: 8,
    margin: 0,
  },

  redPickerButton: {
    borderTopLeftRadius: 8,
    borderTopRightRadius: 8,
    borderBottomLeftRadius: 0,
    borderBottomRightRadius: 0,
  },
  yellowPickerButton: {
    borderRadius: 0,
  },
  greenPickerButton: {
    borderTopLeftRadius: 0,
    borderTopRightRadius: 0,
    borderBottomLeftRadius: 8,
    borderBottomRightRadius: 8,
  },
  titleStyle: {
    flex: 1,
    color: Colors.black,
    padding: 0,
    margin: 0,
    fontSize: 18,
    fontWeight: '600',
    textAlignVertical: 'bottom',
  },
  infoRow: {
    flexDirection: 'row',
    width: '100%',
    height: 14,
    justifyContent: 'flex-start',
    alignItems: 'center',
    padding: 0,
  },
  titleRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'flex-start',
    height: 25,
    marginBottom: 2,
  },
  infoText: {
    color: Colors.grayFill,
    textAlignVertical: 'center',
    fontSize: 12,
    fontWeight: '500',
    letterSpacing: -0.4,
  },
  menuContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  pinnedIcon: {
    width: 20,
    height: 20,
    backfaceVisibility: 'hidden',
  },
});

const FIND_WORKFLOWS = `
  query FindWorkflows($filter: WorkflowFilter) {
    findWorkflows(filter: $filter) {
      count
      items {
        id
        name
        workspace {
          id
        }
        trigger {
          type
          config
        }
        action {
          type
          config
        }
      }
    }
  }
`;
